import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useInView, useReducedMotion } from "framer-motion";
import { BadgeCheck, ChartLine, Check, Video } from "lucide-react";
import { QuiltLogo } from "../QuiltLogo";
import { Clicky, PlayButton, Pointer, ScreenFrame, ScreenStage } from "./ScreenStage";
import cafe from "@/assets/features/review/cafe.webp";
import yoga from "@/assets/features/review/yoga-thumb.webp";
import forest from "@/assets/features/review/forest-thumb.webp";
import reeds from "@/assets/features/review/reeds-thumb.webp";
import origami from "@/assets/features/review/origami-thumb.webp";

/*
 * A live rebuild of the Quilt Pro review queue. On a loop, a pretend pointer approves the video,
 * the tags step opens, it clicks Next, and the queue moves on. Every button works for the
 * visitor too; when they click, the loop waits for them and picks up again once they stop.
 */

type Decision = "approve" | "reject" | null;

interface QueueItem {
    image: string;
    date: string;
    condition: string[];
    symptoms: string[];
    purpose: string[];
}

// Demo content only
const QUEUE: QueueItem[] = [
    { image: cafe, date: "25 Sep, 2026", condition: ["Depressive Disorders", "Social Anxiety Disorder"], symptoms: ["Low mood"], purpose: ["Calming", "Reassuring"] },
    { image: yoga, date: "25 Sep, 2026", condition: ["Generalized Anxiety Disorder"], symptoms: ["Restlessness"], purpose: ["Encouraging", "Energizing"] },
    { image: forest, date: "24 Sep, 2026", condition: ["Generalized Anxiety Disorder"], symptoms: ["Racing thoughts"], purpose: ["Grounding", "Calming"] },
    { image: reeds, date: "24 Sep, 2026", condition: ["Insomnia"], symptoms: ["Trouble falling asleep"], purpose: ["Soothing"] },
    { image: origami, date: "23 Sep, 2026", condition: ["Social Anxiety Disorder"], symptoms: ["Avoidance"], purpose: ["Reassuring", "Hopeful"] },
];

const QUEUE_SIZE = 15;
const THUMB_TOP = 246;
const THUMB_STEP = 128;

// Where the pretend pointer rests and what it clicks, in artboard pixels (tip of the arrow)
const POINT = {
    rest: { x: 560, y: 700 },
    approve: { x: 780, y: 376 },
    next: { x: 1050, y: 812 },
};

const ease = [0.22, 1, 0.36, 1] as const;

function Sidebar() {
    const items = [Video, BadgeCheck, ChartLine];
    return (
        <>
            <div className="absolute inset-y-0 left-0 w-[88px] border-r border-zinc-200 bg-white/60 dark:border-white/[0.06] dark:bg-black/30" />
            <QuiltLogo alt="" className="absolute left-[21px] top-[74px] size-[46px]" />
            <div className="absolute left-0 right-0 top-[145px] h-px bg-zinc-200 dark:bg-white/[0.07]" />
            <div className="absolute left-1 top-[161px] flex w-20 flex-col gap-[5px]">
                {items.map((Icon, i) => (
                    <Clicky
                        key={i}
                        ring="rounded-xl"
                        className={`flex h-12 items-center justify-center rounded-xl transition-colors ${i === 1
                            ? "bg-zinc-100 text-zinc-900 dark:bg-white/[0.07] dark:text-white"
                            : "text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 dark:text-slate-500 dark:hover:bg-white/[0.05] dark:hover:text-slate-200"
                            }`}
                    >
                        <Icon className="size-6" strokeWidth={1.5} />
                    </Clicky>
                ))}
            </div>
        </>
    );
}

function Chip({ children }: { children: string }) {
    return <span className="flex h-7 items-center rounded-full bg-zinc-100 px-3 text-[14px] text-zinc-600 dark:bg-white/[0.07] dark:text-slate-300">{children}</span>;
}

function TagGroup({ title, tags, tone, index }: { title: string; tags: string[]; tone: "teal" | "purple"; index: number }) {
    const tagTone =
        tone === "teal"
            ? "border-teal-500/25 text-teal-700 dark:border-teal-300/15 dark:text-teal-300"
            : "border-purple-500/25 text-purple-700 dark:border-purple-300/15 dark:text-purple-300";
    return (
        <motion.div
            className="h-[88px] rounded-2xl border border-zinc-200 px-4 pt-3.5 dark:border-white/[0.08]"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 + index * 0.12, duration: 0.45, ease }}
        >
            <p className="text-[15px] text-zinc-800 dark:text-slate-200">
                {title}
                <span className="ml-2 text-[13px] text-zinc-400 dark:text-slate-500">• Tagged during creation</span>
            </p>
            <div className="mt-2.5 flex gap-2">
                {tags.map((t) => (
                    <span key={t} className={`flex h-[29px] items-center rounded-full border px-2.5 text-[13px] ${tagTone}`}>
                        {t}
                    </span>
                ))}
            </div>
        </motion.div>
    );
}

function Screen({
    index,
    decision,
    done,
    approved,
    pointer,
    act,
}: {
    index: number;
    decision: Decision;
    done: boolean;
    approved: number[];
    pointer: { x: number; y: number; pressing: boolean; visible: boolean };
    act: { pick: (i: number) => void; decide: (d: Decision) => void; next: () => void };
}) {
    const item = QUEUE[index];
    return (
        <ScreenFrame>
            <Sidebar />
            <div className="absolute left-[120px] top-[64px]">
                <h3 className="text-[28px] font-semibold text-zinc-900 dark:text-white">Review Content</h3>
                <p className="mt-1.5 text-[16px] text-zinc-500 dark:text-slate-500">Review every piece of content.</p>
            </div>

            {/* The review workspace */}
            <div className="absolute left-[121px] top-[170px] h-[718px] w-[1046px] rounded-[40px] border border-zinc-200 bg-white/70 shadow-[0_24px_60px_-28px_rgba(15,23,42,0.25)] dark:border-white/[0.07] dark:bg-[#0D0C19]/80 dark:shadow-[0_30px_70px_-24px_rgba(0,0,0,0.7)]" />

            {/* Queue */}
            <div className="absolute left-[146px] top-[195px] h-[668px] w-[111px] overflow-hidden rounded-[20px] border border-zinc-200 dark:border-white/[0.07]">
                <div className="absolute inset-x-0 top-[15px] flex justify-center">
                    <span className="flex h-[21px] items-center rounded-full bg-zinc-100 px-2.5 text-[13px] text-zinc-500 dark:bg-white/[0.07] dark:text-slate-400">
                        {QUEUE_SIZE - approved.length} Videos
                    </span>
                </div>
            </div>
            {QUEUE.map((q, i) => (
                <Clicky
                    key={i}
                    ring="rounded-[10px]"
                    onClick={() => act.pick(i)}
                    className="group/thumb absolute left-[161px] h-[120px] w-20 overflow-hidden rounded-[10px]"
                    style={{ top: THUMB_TOP + i * THUMB_STEP }}
                >
                    <img
                        src={q.image}
                        alt=""
                        draggable={false}
                        className={`size-full object-cover transition-[filter,transform] duration-300 group-hover/thumb:scale-105 ${i === index ? "" : "brightness-[0.55] group-hover/thumb:brightness-90"}`}
                    />
                    <AnimatePresence>
                        {approved.includes(i) && (
                            <motion.span
                                className="absolute right-1.5 top-1.5 flex size-5 items-center justify-center rounded-full bg-emerald-500 text-white"
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                exit={{ scale: 0 }}
                                transition={{ type: "spring", stiffness: 500, damping: 20 }}
                            >
                                <Check className="size-3" strokeWidth={3.5} />
                            </motion.span>
                        )}
                    </AnimatePresence>
                </Clicky>
            ))}
            {/* The selection ring travels down the queue */}
            <motion.svg
                className="pointer-events-none absolute left-[158px] h-[126px] w-[86px]"
                viewBox="0 0 86 126"
                initial={false}
                animate={{ top: THUMB_TOP - 3 + index * THUMB_STEP }}
                transition={{ type: "spring", stiffness: 260, damping: 28 }}
            >
                <defs>
                    <linearGradient id="review-ring" x1="0" y1="0" x2="0.6" y2="1">
                        <stop stopColor="#F725B1" />
                        <stop offset="0.55" stopColor="#8E0EF0" />
                        <stop offset="1" stopColor="#00ECED" />
                    </linearGradient>
                </defs>
                <rect x="1.5" y="1.5" width="83" height="123" rx="12" fill="none" stroke="url(#review-ring)" strokeWidth="2.5" />
            </motion.svg>

            {/* Status and details */}
            <div className="absolute left-[281px] top-[194px] h-7 overflow-hidden">
                <AnimatePresence mode="wait" initial={false}>
                    <motion.p
                        key={done ? "done" : "pending"}
                        className={`text-[19px] font-semibold ${done ? "text-emerald-600 dark:text-[#22C55E]" : "text-amber-600 dark:text-[#EAB308]"}`}
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: -20, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                    >
                        {done ? "Approved" : "Pending review"}
                    </motion.p>
                </AnimatePresence>
            </div>
            <div className="absolute right-[57px] top-[194px] flex gap-2.5">
                <Chip>EMI</Chip>
                <Chip>Generated</Chip>
                <Chip>{item.date}</Chip>
            </div>

            {/* The video under review */}
            <div className="absolute left-[281px] top-[246px] h-[618px] w-[362px] overflow-hidden rounded-2xl bg-zinc-200 dark:bg-black">
                <AnimatePresence initial={false}>
                    <motion.img
                        key={index}
                        src={item.image}
                        alt=""
                        draggable={false}
                        className="absolute inset-0 size-full object-cover"
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -40 }}
                        transition={{ duration: 0.6, ease }}
                    />
                </AnimatePresence>
                <div className="absolute inset-0 flex items-center justify-center">
                    <Clicky>
                        <PlayButton size={76} />
                    </Clicky>
                </div>
            </div>

            {/* The review form */}
            <div className="absolute left-[667px] top-[246px] h-[618px] w-[476px] overflow-hidden rounded-2xl bg-zinc-50 dark:bg-white/[0.04]">
                <div className="px-5 pt-6">
                    <p className="text-[24px] font-medium text-zinc-700 dark:text-slate-300">Review Video</p>
                    <p className="text-[13px] text-zinc-400 dark:text-slate-500">Reviewing as Jordan Lee</p>
                    <p className="mt-5 text-[15px] font-medium text-zinc-800 dark:text-slate-200">Decision *</p>
                    <div className="mt-2.5 grid grid-cols-2 gap-2.5">
                        <Clicky
                            onClick={() => act.decide("approve")}
                            className={`flex h-11 items-center justify-center rounded-full border text-[16px] transition-colors duration-300 ${decision === "approve"
                                ? "border-emerald-500/70 bg-emerald-500/10 text-emerald-800 dark:border-[#22C55E]/60 dark:bg-[#22C55E]/[0.08] dark:text-slate-100"
                                : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300 dark:border-white/[0.08] dark:bg-white/[0.06] dark:text-slate-300 dark:hover:bg-white/[0.09]"
                                }`}
                        >
                            Approve
                        </Clicky>
                        <Clicky
                            onClick={() => act.decide("reject")}
                            className={`flex h-11 items-center justify-center rounded-full border text-[16px] transition-colors duration-300 ${decision === "reject"
                                ? "border-red-500/60 bg-red-500/10 text-red-800 dark:border-red-400/50 dark:bg-red-400/[0.08] dark:text-slate-100"
                                : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300 dark:border-white/[0.08] dark:bg-white/[0.06] dark:text-slate-300 dark:hover:bg-white/[0.09]"
                                }`}
                        >
                            Reject
                        </Clicky>
                    </div>
                </div>

                {/* Step 2 opens once the video is approved */}
                <AnimatePresence>
                    {decision === "approve" && (
                        <motion.div
                            key={`tags-${index}`}
                            className="absolute inset-x-0 top-[184px] bottom-0"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0, transition: { duration: 0.2 } }}
                        >
                            <div className="mx-5 h-px bg-zinc-200 dark:bg-white/[0.08]" />
                            <p className="mx-5 mt-6 text-[15px] font-medium text-zinc-800 dark:text-slate-200">Step 2: Tags *</p>
                            <div className="mx-5 mt-2.5 grid grid-cols-3 gap-3">
                                {[0, 1, 2].map((b) => (
                                    <span key={b} className="h-[3px] overflow-hidden rounded-full bg-zinc-200 dark:bg-white/[0.08]">
                                        {b < 2 && (
                                            <motion.span
                                                className="block h-full origin-left rounded-full bg-zinc-500 dark:bg-slate-400"
                                                initial={{ scaleX: b === 0 ? 1 : 0 }}
                                                animate={{ scaleX: 1 }}
                                                transition={{ delay: 0.15, duration: 0.6, ease }}
                                            />
                                        )}
                                    </span>
                                ))}
                            </div>
                            <div className="ml-5 mr-0 mt-5 flex flex-col gap-5 pr-0">
                                <TagGroup index={0} title="Condition" tags={item.condition} tone="teal" />
                                <TagGroup index={1} title="Symptoms" tags={item.symptoms} tone="teal" />
                                <TagGroup index={2} title="Emotional Purpose" tags={item.purpose} tone="purple" />
                            </div>

                            {/* Actions float over the end of the list */}
                            <div className="absolute inset-x-0 bottom-0 h-[110px] bg-gradient-to-t from-zinc-50 via-zinc-50/90 to-transparent dark:from-[#101019] dark:via-[#101019]/90" />
                            <div className="absolute inset-x-5 bottom-5 flex justify-between">
                                <Clicky
                                    onClick={() => act.decide(null)}
                                    className="flex h-10 w-[120px] items-center justify-center rounded-full bg-zinc-200 text-[16px] text-zinc-700 transition-colors hover:bg-zinc-300 dark:bg-white/[0.08] dark:text-slate-200 dark:hover:bg-white/[0.12]"
                                >
                                    Back
                                </Clicky>
                                <Clicky
                                    onClick={act.next}
                                    className="flex h-10 w-[120px] items-center justify-center overflow-hidden rounded-full bg-[linear-gradient(90deg,#7316D0,#8C00E2_27%,#5021FB)] text-[16px] font-medium text-white transition-[filter] hover:brightness-110"
                                >
                                    Next
                                </Clicky>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            <Pointer {...pointer} />
        </ScreenFrame>
    );
}

export function ReviewContentMock({ reserve }: { reserve?: string }) {
    const [index, setIndex] = useState(0);
    const [decision, setDecision] = useState<Decision>(null);
    const [done, setDone] = useState(false);
    const [approved, setApproved] = useState<number[]>([]);
    const [pointer, setPointer] = useState({ ...POINT.rest, pressing: false, visible: false });
    const [cycle, setCycle] = useState(0);
    const [paused, setPaused] = useState(false);

    const wrap = useRef<HTMLDivElement>(null);
    const inView = useInView(wrap, { amount: 0.5 });
    const reduceMotion = useReducedMotion();
    const playing = inView && !reduceMotion && !paused;
    const resume = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    // Approve and move on: mark it done, then bring up the next video in the queue
    const advance = (from: number) => {
        setDone(true);
        setApproved((a) => (a.includes(from) ? a : [...a, from]));
        return setTimeout(() => {
            const nextIndex = (from + 1) % QUEUE.length;
            setIndex(nextIndex);
            setDecision(null);
            setDone(false);
            if (nextIndex === 0) setApproved([]);
        }, 900);
    };

    // The demo: point at Approve, click, wait on the tags, point at Next, click, repeat
    useEffect(() => {
        if (!playing) return;
        const timers: ReturnType<typeof setTimeout>[] = [];
        const at = (ms: number, fn: () => void) => timers.push(setTimeout(fn, ms));
        const click = (fn: () => void) => {
            setPointer((p) => ({ ...p, pressing: true }));
            timers.push(setTimeout(() => {
                setPointer((p) => ({ ...p, pressing: false }));
                fn();
            }, 140));
        };
        setDecision(null);
        setDone(false);
        at(300, () => setPointer({ ...POINT.rest, pressing: false, visible: true }));
        at(700, () => setPointer((p) => ({ ...p, ...POINT.approve })));
        at(1900, () => click(() => setDecision("approve")));
        at(4900, () => setPointer((p) => ({ ...p, ...POINT.next })));
        at(6100, () => click(() => timers.push(advance(index))));
        at(7200, () => setPointer((p) => ({ ...p, ...POINT.rest })));
        at(7900, () => setCycle((c) => c + 1));
        return () => timers.forEach(clearTimeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playing, cycle]);

    // A visitor's click takes over; the demo waits, then carries on from where they left it
    const takeOver = () => {
        setPaused(true);
        setPointer((p) => ({ ...p, visible: false, pressing: false }));
        clearTimeout(resume.current);
        resume.current = setTimeout(() => {
            setPaused(false);
            setCycle((c) => c + 1);
        }, 8000);
    };
    useEffect(() => () => clearTimeout(resume.current), []);

    const act = {
        pick: (i: number) => {
            takeOver();
            setIndex(i);
            setDecision(null);
            setDone(false);
        },
        decide: (d: Decision) => {
            takeOver();
            setDecision(d);
        },
        next: () => {
            takeOver();
            advance(index);
        },
    };

    return (
        <div ref={wrap}>
            <ScreenStage reserve={reserve} label="Quilt Pro review queue: a clinician watches each generated video, approves or rejects it, then checks its condition, symptom and emotional-purpose tags.">
                {() => <Screen index={index} decision={decision} done={done} approved={approved} pointer={pointer} act={act} />}
            </ScreenStage>
        </div>
    );
}

export default ReviewContentMock;
